/**
 * Dashboard — what is waiting, and what the year has looked like.
 *
 * **A scope note, because this page sits on a line the plan draws.** Plan D10
 * defers "reporting and dashboards" to Parinaam (§1.20) and §13.53 lists
 * advanced dashboards under stop-building. Parinaam asked for KPIs and charts
 * anyway, referring to the prototype's Metrics Dashboard, so they are here — and
 * the boundary that keeps this from quietly becoming the reporting product is
 * written down in `shared/src/metrics.ts`: one screen, one request, no
 * drill-down, no export.
 *
 * **The period control (Parinaam, 9 Oct 2026.)** The figures split in two and
 * the screen has to say which is which, because a range that silently moves
 * some numbers and not others is worse than no range at all. "Hours recorded"
 * answers the window; "Volunteers by status" cannot — a status has no date. So
 * the cards that answer the window carry the window in their title, and the
 * ones that do not say "as of today".
 *
 * The page reads top to bottom as a shift starts:
 *
 *   1. **Waiting for a decision** — work nobody has done yet, each figure a link
 *      to the screen where it gets done. This is the part that is not a report,
 *      and it stays first because it is the part somebody can act on — and
 *      because it is true whatever period the rest of the page is showing.
 *   2. **Period** — the window everything below it answers. Here rather than at
 *      the top, so the card above it is not read as filtered.
 *   3. **The numbers** — counts, including recorded hours, which §4.29 makes
 *      *the* reporting output.
 *   4. **The charts** — where the volunteers are in their lifecycle, where the
 *      hours came from, and where a roster and its attendance disagree.
 *
 * Charts are inline SVG with no charting library (Plan D8 declined a component
 * library; see `components/charts/Charts.tsx`). Every figure comes from
 * `GET /metrics/dashboard` in a single round trip.
 *
 * It remains the reference implementation of the §13.42–13.43 rule: loading,
 * error and empty on every fetching surface, guarding on `isPending` rather than
 * `isLoading` (see `States.tsx`).
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { VOLUNTEER_STATUS, formatDuration } from '@parinaam-vms/shared';
import type { DashboardMetrics } from '@parinaam-vms/shared';
import {
  Card,
  DateRangePicker,
  ErrorState,
  LoadingState,
  PageHeader,
} from '../../components/common';
import type { DateRange } from '../../components/forms/DateRangePicker';
import { ColumnChart, PieChart } from '../../components/charts/Charts';
import { useActivities } from '../../hooks/usePrograms';
import { useVolunteers } from '../../hooks/useVolunteers';
import { useDashboardMetrics } from '../../hooks/useMetrics';
import { useHealth } from '../../hooks/useHealth';
import { formatDate, humaniseCode } from '../../utils/format';
import styles from './DashboardPage.module.css';

/** `YYYY-MM-DD`, the form every date filter in this application takes. */
function isoDay(offsetDays = 0): string {
  const date = new Date();
  date.setDate(date.getDate() + offsetDays);
  return date.toISOString().slice(0, 10);
}

/** One row of a list endpoint is enough — the count comes from `pagination`. */
const COUNT_ONLY = { page_size: 1 } as const;

/** `2026-08` → `Aug 26`. Axis ticks have no room for a full month name. */
function shortMonth(month: string): string {
  const [year, index] = month.split('-');
  const name = new Date(Number(year), Number(index) - 1, 1).toLocaleString('en-GB', {
    month: 'short',
  });
  return `${name} ${year?.slice(2)}`;
}

/**
 * How a window reads in a card's title: "(1 Jan – 31 Mar 2026)".
 *
 * Returns '' for all time, which is the default and needs no caption — a title
 * reading "Hours recorded (all time)" on a dashboard nobody has filtered is
 * noise explaining the absence of a thing.
 */
function periodCaption(range: DateRange): string {
  if (!range.from && !range.to) return '';
  if (range.from && range.to) return ` (${formatDate(range.from)} – ${formatDate(range.to)})`;
  if (range.from) return ` (since ${formatDate(range.from)})`;
  return ` (up to ${formatDate(range.to)})`;
}

export function DashboardPage() {
  /*
   * The window lives here rather than in each card.
   *
   * Two components below read the metrics, and both have to ask for the same
   * window or the page would show two periods at once. React Query dedupes on
   * the key, so passing the range down is one request, not two.
   *
   * Starts empty — all time — so the dashboard opens on the same figures it
   * always has. A landing screen that starts filtered is a landing screen that
   * hides something from somebody who did not ask it to.
   */
  const [range, setRange] = useState<DateRange>({ from: '', to: '' });

  return (
    <>
      <PageHeader title="Dashboard" />

      {/*
        Waiting first, then the period, then everything the period governs.

        The control sat above "Waiting for a decision" until Parinaam moved it
        (9 Oct 2026), and below is the better place for the reason the page's
        docblock gives: what is waiting is the one card here that is not a
        report, and it is acted on whatever window somebody is looking at. A
        filter above it implies otherwise.
      */}
      <WaitingCard />

      <Card title="Period">
        <DateRangePicker
          value={range}
          onChange={setRange}
          label="Period for the dashboard figures"
        />
        <p className={styles.note}>
          Hours, turnout and charts answer this window.
        </p>
      </Card>

      <NumbersCard range={range} />
      <ChartsSection range={range} />

      <div className={styles.grid}>
        <ComingUpCard />
        <SystemStatusCard />
      </div>
    </>
  );
}

/**
 * The place the golden path can stall.
 *
 * A submission nobody reviews is a volunteer who never starts, and it is
 * invisible from every other screen because each of those shows its *own* work.
 *
 * There were two tiles here until 8 Oct 2026. The other counted hours waiting
 * for approval, which was the second way the path stalled - and approval was
 * removed, so nothing can wait. A tile that reads "nothing waiting" every day
 * for ever teaches people to stop looking at the card.
 */
function WaitingCard() {
  const submissions = useVolunteers({ status: VOLUNTEER_STATUS.PENDING, ...COUNT_ONLY });

  return (
    <Card title="Waiting for a decision">
      <div className={styles.tiles}>
        <StatTile
          label="Submissions to review"
          query={submissions}
          to="/volunteers"
          action="Open volunteers"
          settled="No submissions waiting"
          hint='Registered, and waiting to be activated.'
        />
      </div>
    </Card>
  );
}

/** `11` of `22` → `50%`. Guarded, because a percentage of nothing is not 0%. */
function percent(part: number, whole: number): string {
  if (whole <= 0) return '—';
  return `${Math.round((part / whole) * 100)}%`;
}

/** Nine figures, from the metrics endpoint. */
function NumbersCard({ range }: { range: DateRange }) {
  const { data, isPending, isError, error, refetch } = useDashboardMetrics(range);
  const t = data?.totals;
  const windowed = Boolean(range.from || range.to);

  return (
    <Card title={`The numbers${periodCaption(range)}`}>
      {isPending || !t ? (
        <LoadingState label="Loading the figures…" />
      ) : isError ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <div className={styles.kpis}>
{/*
            STANDING — "how many volunteers are on file between January and
            March" is not a question, so these two say what they are rather than
            appearing to answer the window beside them.
          */}
          <Kpi
            label="Volunteers"
            value={t.volunteers}
            note={`${t.volunteers_pending} awaiting review · as of today`}
          />

          <Kpi
            label="Active"
            value={t.volunteers_active}
            note={`${percent(t.volunteers_active, t.volunteers)} of everyone on file · as of today`}
          />

          {/*
            The gap between this and "Active" is the reading. ACTIVE is a status
            somebody set, possibly a year ago; this is who actually turned up.
          */}
          <Kpi
            label={windowed ? 'Turned up in this period' : 'Active in 30 days'}
            value={t.volunteers_recently_active}
            note={`${percent(t.volunteers_recently_active, t.volunteers_active)} of active volunteers`}
            tone={t.volunteers_recently_active > 0 ? 'up' : 'neutral'}
          />

          {/* The most actionable figure here, and invisible on every other screen. */}
          {/*
            STANDING, and "ever" is the reason: "never asked during March" is
            not the same concern at all — somebody asked in February and not
            since has not been overlooked.
          */}
          <Kpi
            label="Not yet placed"
            value={t.volunteers_never_assigned}
            note={
              t.volunteers_never_assigned > 0
                ? 'Active, but never assigned to anything · ever'
                : 'Everybody active has been assigned'
            }
            tone={t.volunteers_never_assigned > 0 ? 'watch' : 'up'}
          />

          <Kpi
            label="Active programs"
            value={t.programs_active}
            note={`${t.activities} activities · as of today`}
          />

          {/*
            The reporting figure (§4.29). Minutes are stored; people read hours.

            It carried a second line - "2h 30m not yet approved", or "everything
            recorded is approved" - and an amber tone when anything was waiting.
            Both went with the approval workflow on 8 Oct 2026: hours are counted
            as they are recorded, so the note said the same reassuring thing for
            ever, which is a line nobody reads and eventually trusts.
          */}
          <Kpi label="Hours recorded" value={formatDuration(t.approved_minutes)} />

          {/*
            A period figure over a standing one, deliberately: hours given in
            the window, spread across the people currently active. The
            alternative — "people active during the window" — is a number this
            data model cannot produce, because a status carries no history.
          */}
          <Kpi
            label="Average per volunteer"
            value={
              t.volunteers_active > 0
                ? formatDuration(Math.round(t.approved_minutes / t.volunteers_active))
                : '—'
            }
            note="Hours recorded, per volunteer active today"
          />

          {/*
            Recorded can exceed expected, and that is not an error — D3 allows
            recording somebody who simply turned up. Shown as two counts rather
            than a percentage, because "108% turnout" reads as a mistake.
          */}
          <Kpi
            label="Turnout"
            value={`${t.recorded_total} / ${t.expected_total}`}
            note="Recorded against roster places"
          />

          {/*
            "Longest wait" stood here - the age of the oldest unapproved record,
            amber past a week. It measured the approval backlog, and there is no
            longer a queue for anything to sit in, so it reported "—" and a
            green tone permanently.

            `oldest_pending_days` and `pending_minutes` are still in the API.
            They describe the records left pending by the old workflow, which
            are real and still in the table; what has gone is presenting them as
            work somebody is expected to go and do.
          */}
        </div>
      )}
    </Card>
  );
}

/**
 * The charts.
 *
 * Four panels, and each answers a question rather than filling a slot. The
 * prototype's other panels — beneficiaries, feedback ratings, training
 * completion, certificates — have no table behind them in this data model, so
 * they are not drawn rather than drawn from invented numbers.
 */
function ChartsSection({ range }: { range: DateRange }) {
  const { data, isPending, isError, error, refetch } = useDashboardMetrics(range);

  if (isPending) {
    return (
      <Card title="Charts">
        <LoadingState label="Loading the charts…" />
      </Card>
    );
  }

  if (isError) {
    return (
      <Card title="Charts">
        <ErrorState error={error} onRetry={() => void refetch()} />
      </Card>
    );
  }

  return (
    <div className={styles.grid}>
      {/*
        Donuts, because both of these are genuinely part-to-whole: every
        volunteer carries exactly one status, so the slices are disjoint and sum
        to the total. Four segments, inside the six a pie can carry.
      */}
      {/* STANDING, both of them — see the STANDING/PERIOD split in shared. */}
      <Card title="Volunteers by status (as of today)">
        <PieChart
          caption="Volunteers by status"
          totalLabel="volunteers"
          slices={toSlices(data.volunteers_by_status)}
          emptyMessage="No volunteers yet."
        />
      </Card>

      <Card title="Activities by status (as of today)">
        <PieChart
          caption="Activities by status"
          totalLabel="activities"
          slices={toSlices(data.activities_by_status)}
          emptyMessage="No activities yet."
        />
      </Card>

      <Card title={`Hours by month${periodCaption(range)}`}>
        {/*
          A chart of one column is not a chart — it restates a number the KPI
          above already gives, with axes around it. Below two months there is no
          shape to read, so the honest answer is a sentence saying so.
        */}
        {data.approved_minutes_by_month.length < 2 ? (
          <p className={styles.note}>
            {data.approved_minutes_by_month.length === 0
              ? range.from || range.to
                ? /*
                   * "No APPROVED hours", not "no hours". The two are different
                   * and the difference is exactly what somebody filtering a
                   * recent period runs into: October 2026 holds attendance
                   * records that nobody has approved, and telling that person
                   * nothing was recorded sends them looking for a bug in the
                   * filter instead of at the records.
                   */
                  'No approved hours in this period.'
                : 'No approved hours yet.'
              : `All of it falls in ${shortMonth(
                  data.approved_minutes_by_month[0]?.month ?? ''
                )}.`}
          </p>
        ) : (
          <ColumnChart
            caption="Approved volunteer hours by month"
            points={data.approved_minutes_by_month.map((month) => ({
              label: shortMonth(month.month),
              value: month.minutes,
            }))}
            formatValue={(minutes) => formatDuration(Math.round(minutes))}
          />
        )}
      </Card>

      {/*
        Recut as part-to-whole so it can honestly be a pie.

        "Expected against recorded" cannot be one: 13 roster places and 14
        records are overlapping measures of the same people, they sum to 27, and
        27 is not a quantity that exists. A pie of the two would print confident
        percentages of an invented total.

        Split by what actually happened to each volunteer-at-an-activity pairing,
        the three parts are disjoint and do sum — and the split says more than
        the pair did, because a no-show and a walk-in stop cancelling out.
      */}
      <Card title={`How participation went${periodCaption(range)}`}>
        <p className={styles.note}>
          Every volunteer-at-an-activity pairing, by outcome.
        </p>
        <PieChart
          caption="Participation by outcome"
          totalLabel="pairings"
          slices={toSlices(data.participation_split)}
          emptyMessage="No activity has a roster or attendance yet."
        />
      </Card>
    </div>
  );
}

/**
 * `ACTIVE` → `Active`, `ROSTERED_NO_SHOW` → `Rostered no show`.
 *
 * Staff never see a database value verbatim (§8.4.4), and a donut leans on its
 * legend harder than a bar chart does — the label is the only thing naming the
 * slice, so an unhumanised code would be the whole reading.
 */
function toSlices(slices: DashboardMetrics['volunteers_by_status']) {
  return slices.map((slice) => ({ label: humaniseCode(slice.key), value: slice.count }));
}

/**
 * The week ahead.
 *
 * Not a metric — a prompt. An activity with nobody assigned to it is the failure
 * this catches, and it is only visible while there is still time to fix it.
 */
function ComingUpCard() {
  const activities = useActivities({ from: isoDay(), to: isoDay(7), ...COUNT_ONLY });

  return (
    <Card title="The next seven days">
      <div className={styles.tiles}>
        <StatTile
          label="Activities scheduled"
          query={activities}
          to="/activities"
          action="Open activities"
          settled="Nothing scheduled this week"
          hint="Check each one has volunteers assigned before the day arrives."
        />
      </div>
    </Card>
  );
}

/** The §2.21 health check, shown rather than curled. */
function SystemStatusCard() {
  const { data, isPending, isError, error, refetch } = useHealth();

  return (
    <Card title="System status">
      {isPending ? (
        <LoadingState label="Checking the API…" />
      ) : isError ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <dl className={styles.definitions}>
          <dt>API</dt>
          <dd>{data?.status === 'ok' ? 'Responding' : 'Degraded'}</dd>

          <dt>Database</dt>
          <dd>{data?.database === 'connected' ? 'Connected' : 'Not reachable'}</dd>

          <dt>Version</dt>
          <dd>{data?.version}</dd>
        </dl>
      )}
    </Card>
  );
}

/**
 * One figure.
 *
 * Proportional figures, not `tabular-nums`: these are standalone display
 * numbers, and tabular gives every digit the width of a zero, which makes a
 * value like 121 look loose at this size. Tabular is for columns that align.
 */
function Kpi({
  label,
  value,
  note,
  tone = 'neutral',
}: {
  label: string;
  value: number | string;
  note?: string;
  /**
   * Colours the context line. Never the only signal — the sentence says which
   * it is, so the colour reinforces rather than carries it.
   */
  tone?: 'up' | 'watch' | 'neutral';
}) {
  const toneClass =
    tone === 'up' ? styles.kpiUp : tone === 'watch' ? styles.kpiWatch : styles.kpiNeutral;

  return (
    <div className={styles.kpi}>
      <p className={styles.kpiLabel}>{label}</p>
      <p className={styles.kpiValue}>{value}</p>
      {note && <p className={`${styles.kpiDelta} ${toneClass}`}>{note}</p>}
    </div>
  );
}

interface StatTileProps {
  label: string;
  /** Any list query — only its `pagination.total` is read. */
  query: {
    data?: { pagination?: { total: number } };
    isPending: boolean;
    isError: boolean;
    error: unknown;
    refetch: () => unknown;
  };
  to: string;
  /** Link text when there is work. */
  action: string;
  /** Shown instead of a number when the count is zero. */
  settled: string;
  hint: string;
}

/**
 * One count, and the way to act on it.
 *
 * A zero renders as a sentence, not as `0`. "0" invites the reader to wonder
 * whether the number failed to load; "No submissions waiting" does not.
 */
function StatTile({ label, query, to, action, settled, hint }: StatTileProps) {
  const { data, isPending, isError, error, refetch } = query;

  if (isPending) return <LoadingState label={`Counting ${label.toLowerCase()}…`} />;
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const total = data?.pagination?.total ?? 0;

  return (
    <div className={styles.tile}>
      <p className={styles.tileLabel}>{label}</p>

      {total === 0 ? (
        <p className={styles.tileSettled}>{settled}</p>
      ) : (
        <p className={styles.tileValue}>{total}</p>
      )}

      <p className={styles.tileHint}>{hint}</p>

      <Link className={styles.tileLink} to={to}>
        {total === 0 ? 'Open' : action} →
      </Link>
    </div>
  );
}
